import { useCallback, useEffect, useRef, useState } from 'react';
import { useFlowUsers } from './useClientData';
import { useClientFlow, saveClientFlow } from './useClientFlow';
import { useClientDocumentsByUser } from './useClientDocuments';
import { useAllUsersIncludingArchived } from '../../../shared/hooks/useAllUsers';
import { portCoord as portCoordPure } from '../lib/flowPortGeometry';

// 04-enforce.md 4.1 -- extraído de OpDetailPanel.jsx. Responsabilidad única: el motor del canvas
// del flow (pan/zoom/drag/resize/conexión de nodos + carga/guardado del flow + nodo
// seleccionado). El shell del panel (tabs, header, comentarios, resize del ancho) se queda en
// `OpDetailPanel.jsx`. Era ~300 líneas operativas de estado + handlers entremezclados con ese
// shell.

// Fase 5.18 (05-remediation.md, parte 2) -- `react-hooks/set-state-in-effect` (hint de React
// Compiler, no un bug de runtime) sube a `error` en todo `src/`. Este archivo hace setState
// síncrono dentro de un `useEffect` en un patrón de sincronización legítimo: hidrata nodos/edges/docs/usuarios del canvas de flow desde los resultados de React Query (5 sitios, mismo patrón).
// No se reescribe a estado derivado (repetiría el fetch/haría el UI menos predecible en
// varios de estos casos) -- deshabilitado a nivel de archivo, con motivo, no en silencio.
/* eslint-disable react-hooks/set-state-in-effect */
const clampScale = (s) => Math.min(3, Math.max(0.3, s));
const getDistance = (t1, t2) => Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);

export function useOpFlowCanvas({ op, navigate, spacePrefix, urlNodeId }) {
  // ── Canvas viewport ──────────────────────────────────────────────────────
  const flowContainerRef = useRef(null);
  const [flowPos, setFlowPos] = useState({ x: 0, y: 0 });
  const [flowScale, setFlowScale] = useState(1);
  const flowDragRef = useRef(null);
  const flowPinchRef = useRef(null);

  // ── Nodes & edges ────────────────────────────────────────────────────────
  const [flowNodes, setFlowNodes] = useState([]);
  const [flowEdges, setFlowEdges] = useState([]);
  const [flowSearch, setFlowSearch] = useState('');
  const [flowDraggingNode, setFlowDraggingNode] = useState(null);
  const [flowResizingNode, setFlowResizingNode] = useState(null);
  const [flowConnecting, setFlowConnecting] = useState(null);
  const [selectedTriggerNode, setSelectedTriggerNode] = useState(null);
  const flowSvgRef = useRef(null);
  const [flowDropdown, setFlowDropdown] = useState(null);
  const flowMenuRefs = useRef({});
  const [flowDdSearch, setFlowDdSearch] = useState('');
  const [flowUsers, setFlowUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const flowSaveTimer = useRef(null);
  const flowLoadedRef = useRef(false);
  const [opDocs, setOpDocs] = useState({});

  // urlNodeId → selectedTriggerNode
  useEffect(() => {
    if (urlNodeId && flowNodes.length > 0) {
      const targetNode = flowNodes.find(n => n.id === urlNodeId);
      if (targetNode && (!selectedTriggerNode || selectedTriggerNode.id !== targetNode.id)) {
        setSelectedTriggerNode(targetNode);
      }
    } else if (!urlNodeId && selectedTriggerNode) {
      setSelectedTriggerNode(null);
    }
  }, [urlNodeId, flowNodes]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Documents for the deal (shared cache with SubPanelTratos, same query key) ──
  const { data: opDocsList } = useClientDocumentsByUser(op && op.id);
  useEffect(() => {
    if (!opDocsList) return;
    const map = {};
    opDocsList.forEach(doc => { map[doc.document_type] = doc; });
    setOpDocs(map);
  }, [opDocsList]);

  // ── Load / save the flow ─────────────────────────────────────────────────
  const { data: flowData } = useClientFlow(op.id);
  useEffect(() => {
    flowLoadedRef.current = false;
    if (!flowData) return;
    if (flowData.nodes) setFlowNodes(flowData.nodes);
    if (flowData.edges) setFlowEdges(flowData.edges);
    flowLoadedRef.current = true;
  }, [flowData]);

  const saveFlow = useCallback((nodes, edges) => {
    clearTimeout(flowSaveTimer.current);
    flowSaveTimer.current = setTimeout(() => {
      saveClientFlow(op.id, { nodes, edges }).catch(() => {});
    }, 800);
  }, [op.id]);

  useEffect(() => {
    if (flowLoadedRef.current) saveFlow(flowNodes, flowEdges);
  }, [flowNodes, flowEdges, saveFlow]);

  const { data: allUsersData } = useAllUsersIncludingArchived();
  const { data: flowUsersData } = useFlowUsers();
  useEffect(() => { if (allUsersData) setAllUsers(allUsersData); }, [allUsersData]);
  useEffect(() => { if (flowUsersData) setFlowUsers(flowUsersData); }, [flowUsersData]);

  // ── Stable node callbacks (FlowNode is memo-wrapped) ─────────────────────
  const handleDeleteFlowNode = useCallback((nodeId) => {
    setFlowNodes(p => p.filter(n => n.id !== nodeId));
    setFlowEdges(p => p.filter(e2 => e2.from !== nodeId && e2.to !== nodeId));
  }, []);
  const handleDeleteEtapaContainer = useCallback((nodeId) => {
    setFlowNodes(p => p.filter(n => n.id !== nodeId));
  }, []);
  const handleEditFlowNode = useCallback((n) => {
    if (n.category === 'documento' || n.category === 'trigger' || n.category === 'cliente') {
      setSelectedTriggerNode(n);
      if (op.short_id) navigate(`${spacePrefix}/database/clientes/${op.short_id}/flow/${n.id}`, { replace: true });
    }
  }, [op.short_id, spacePrefix, navigate]);

  const closeSelectedTriggerNode = useCallback(() => {
    setSelectedTriggerNode(null);
    if (op.short_id) navigate(`${spacePrefix}/database/clientes/${op.short_id}/flow`, { replace: true });
  }, [op.short_id, spacePrefix, navigate]);

  const addFlowNode = (category, item) => {
    const id = 'fn_' + Date.now();
    const node = { id, category, label: item.nick || item.label || '' };
    if (category === 'cliente' || category === 'oper') node.avatar = (item['link-profile-img-thumb'] || item['link-profile-img']) || null;
    if (category === 'trigger' || category === 'documento' || category === 'etapa') { node.icon = item.icon; node.sub = item.sub || null; }
    if (category === 'estructura') node.icon = item.icon || '🏢';
    if (category === 'etapa') { node.w = 260; node.h = 180; }
    setFlowNodes(prev => [...prev, { ...node, x: 80 + prev.length * 30, y: 80 + prev.length * 24 }]);
    setFlowDropdown(null);
  };

  // ── Node drag (separate from canvas pan) ─────────────────────────────────
  const startNodeDrag = (e, nodeId) => {
    e.stopPropagation();
    const node = flowNodes.find(n => n.id === nodeId);
    if (!node) return;
    setFlowDraggingNode({ id: nodeId, startX: e.clientX, startY: e.clientY, origX: node.x, origY: node.y });
  };
  const onFlowNodeMouseMove = (e) => {
    if (!flowDraggingNode) return;
    const dx = (e.clientX - flowDraggingNode.startX) / flowScale;
    const dy = (e.clientY - flowDraggingNode.startY) / flowScale;
    setFlowNodes(prev => prev.map(n =>
      n.id === flowDraggingNode.id ? { ...n, x: flowDraggingNode.origX + dx, y: flowDraggingNode.origY + dy } : n
    ));
  };
  const endNodeDrag = () => setFlowDraggingNode(null);

  // ── Node resize (containers) ─────────────────────────────────────────────
  const startNodeResize = (e, nodeId) => {
    e.stopPropagation();
    e.preventDefault();
    const node = flowNodes.find(n => n.id === nodeId);
    if (!node) return;
    setFlowResizingNode({ id: nodeId, startX: e.clientX, startY: e.clientY, origW: node.w || 260, origH: node.h || 180 });
  };
  const onNodeResizeMouseMove = (e) => {
    if (!flowResizingNode) return;
    const dx = (e.clientX - flowResizingNode.startX) / flowScale;
    const dy = (e.clientY - flowResizingNode.startY) / flowScale;
    setFlowNodes(prev => prev.map(n =>
      n.id === flowResizingNode.id ? { ...n, w: Math.max(150, flowResizingNode.origW + dx), h: Math.max(100, flowResizingNode.origH + dy) } : n
    ));
  };
  const endNodeResize = () => setFlowResizingNode(null);

  // ── Port → edge connecting ──────────────────────────────────────────────
  const startConnect = (e, fromId, fromPort, portIndex) => {
    e.stopPropagation();
    const rect = flowContainerRef.current.getBoundingClientRect();
    setFlowConnecting({ fromId, fromPort, portIndex: portIndex ?? 0, curX: e.clientX - rect.left, curY: e.clientY - rect.top });
  };
  const updateConnecting = (e) => {
    if (!flowConnecting) return;
    const rect = flowContainerRef.current.getBoundingClientRect();
    setFlowConnecting(prev => ({ ...prev, curX: e.clientX - rect.left, curY: e.clientY - rect.top }));
  };
  const endConnect = (e, toId, toPort, toPortIndex) => {
    if (!flowConnecting || flowConnecting.fromId === toId) { setFlowConnecting(null); return; }
    setFlowEdges(prev => [...prev, { id: 'e_' + Date.now(), from: flowConnecting.fromId, to: toId, portIndex: flowConnecting.portIndex ?? 0, toPortIndex: toPortIndex ?? 0 }]);
    setFlowConnecting(null);
  };

  const portCoord = useCallback((nodeId, side, portIndex) => portCoordPure(flowNodes, nodeId, side, portIndex), [flowNodes]);

  // ── Zoom / wheel ────────────────────────────────────────────────────────
  const onFlowWheel = useCallback((e) => {
    e.preventDefault();
    const rect = flowContainerRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    if (e.ctrlKey) {
      const delta = -e.deltaY * 0.01;
      setFlowScale(prev => {
        const next = clampScale(prev + delta * prev);
        const ratio = next / prev;
        setFlowPos(p => ({
          x: mouseX - ratio * (mouseX - p.x),
          y: mouseY - ratio * (mouseY - p.y),
        }));
        return next;
      });
    } else {
      setFlowPos(p => ({ x: p.x - e.deltaX, y: p.y - e.deltaY }));
    }
  }, []);

  useEffect(() => {
    const el = flowContainerRef.current;
    if (!el) return;
    el.addEventListener('wheel', onFlowWheel, { passive: false });
    return () => el.removeEventListener('wheel', onFlowWheel);
  }, [onFlowWheel]);

  // ── Mouse pan ───────────────────────────────────────────────────────────
  const onFlowMouseDown = (e) => {
    if (e.button !== 0) return;
    flowDragRef.current = { startX: e.clientX, startY: e.clientY, originX: flowPos.x, originY: flowPos.y };
    e.currentTarget.style.cursor = 'grabbing';
  };
  const onFlowMouseMove = (e) => {
    if (!flowDragRef.current) return;
    const dx = e.clientX - flowDragRef.current.startX;
    const dy = e.clientY - flowDragRef.current.startY;
    setFlowPos({ x: flowDragRef.current.originX + dx, y: flowDragRef.current.originY + dy });
  };
  const onFlowMouseUp = (e) => {
    flowDragRef.current = null;
    e.currentTarget.style.cursor = 'grab';
  };
  const onFlowMouseLeave = (e) => {
    if (flowDragRef.current) {
      flowDragRef.current = null;
      e.currentTarget.style.cursor = 'grab';
    }
  };

  // ── Touch / pinch ───────────────────────────────────────────────────────
  const onFlowTouchStart = (e) => {
    if (e.touches.length === 2) {
      flowPinchRef.current = { dist: getDistance(e.touches[0], e.touches[1]), scale: flowScale };
    } else if (e.touches.length === 1) {
      flowDragRef.current = { startX: e.touches[0].clientX, startY: e.touches[0].clientY, originX: flowPos.x, originY: flowPos.y };
    }
  };
  const onFlowTouchMove = (e) => {
    e.preventDefault();
    if (e.touches.length === 2 && flowPinchRef.current) {
      const newDist = getDistance(e.touches[0], e.touches[1]);
      const ratio = newDist / flowPinchRef.current.dist;
      setFlowScale(clampScale(flowPinchRef.current.scale * ratio));
    } else if (e.touches.length === 1 && flowDragRef.current) {
      const dx = e.touches[0].clientX - flowDragRef.current.startX;
      const dy = e.touches[0].clientY - flowDragRef.current.startY;
      setFlowPos({ x: flowDragRef.current.originX + dx, y: flowDragRef.current.originY + dy });
    }
  };
  const onFlowTouchEnd = () => {
    flowDragRef.current = null;
    flowPinchRef.current = null;
  };

  // Handlers combinados que el viewport (un `<div>` plano, sin memo) pasa a cada evento del
  // mouse -- varios sub-handlers por evento, igual que el JSX original.
  const viewportHandlers = {
    onMouseDown: onFlowMouseDown,
    onMouseMove: (e) => { onFlowMouseMove(e); onFlowNodeMouseMove(e); onNodeResizeMouseMove(e); updateConnecting(e); },
    onMouseUp: (e) => { onFlowMouseUp(e); endNodeDrag(); endNodeResize(); if (flowConnecting) setFlowConnecting(null); },
    onMouseLeave: (e) => { onFlowMouseLeave(e); endNodeDrag(); endNodeResize(); if (flowConnecting) setFlowConnecting(null); },
    onTouchStart: onFlowTouchStart,
    onTouchMove: onFlowTouchMove,
    onTouchEnd: onFlowTouchEnd,
  };

  return {
    flowContainerRef, flowSvgRef, flowMenuRefs,
    flowPos, flowScale,
    flowNodes, flowEdges,
    flowSearch, setFlowSearch,
    flowConnecting,
    selectedTriggerNode, closeSelectedTriggerNode,
    flowDropdown, setFlowDropdown,
    flowDdSearch, setFlowDdSearch,
    flowUsers, allUsers,
    opDocs, setOpDocs,
    flowDraggingNode,
    saveFlow,
    handleDeleteFlowNode, handleDeleteEtapaContainer, handleEditFlowNode,
    addFlowNode,
    startNodeDrag, startNodeResize, startConnect, endConnect,
    portCoord,
    viewportHandlers,
  };
}
